import {
    Infobox,
    Text,
    TextInput,
    TextTag,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'

import axios from 'axios'

import React, {useState} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {UserDataForm} from '../../types/userData'
import {RootState} from '../rootReducer'
import {Loading} from '../components/loading'
import {openAlertSystemLayer} from '../layers/launchers/AlertSystemLayerHandle'
import {showThanksRecoverPage} from '../../features/userData/userDataSlice'
import {AppDispatch} from '../thunk'
import {
    Endpoints,
    Global,
    InfoboxStyle,
    InputStyle,
} from '../../shared/variables'
import {
    defaultModel,
    eventInfoCommon,
    getDealerUserInfo,
    TrackingAction,
    trackingData,
    trackingEvent,
} from '../../shared/trackingServiceHelper'
import {LayerForm} from '../../shared/classes'
import store from '../store'
import {ErrorSummary} from './ErrorSummary'

const FieldContainer = styled.div`
    margin: 0 0 ${(props) => props.theme.size.dynamic0100};
`

export const RECOVER_COUPON_FORM_ID = 'recover-coupon-form'

export function RecoverCouponForm(): React.JSX.Element {
    const {t} = useTranslation('fa-coupon')
    const dispatch = useDispatch() as AppDispatch

    const [loadingRecoverForm, setLoadingRecoverForm] = useState(false)
    const [showNotification, setShowNotification] = useState(false)
    const [NotificationText, setNotificationText] = useState('')

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const campaignCode = useSelector(
        (state: RootState) => state.general.campaignCode
    )
    const cycleID = useSelector(
        (state: RootState) => state.userData.formCycleID
    )
    const configurationPromo = useSelector(
        (state: RootState) => state.configurationPromo.configuration
    )

    const recoveryTitle = configurationPromo.coupon.recovery?.title
        ? configurationPromo.coupon.recovery.title
        : t('layers.recoverCouponRequest.title')
    const recoveryCTA = configurationPromo.coupon.recovery?.cta
        ? configurationPromo.coupon.recovery.cta
        : t('recover.submit')

    const {
        register,
        setError,
        handleSubmit,
        reset,
        formState,
        formState: {errors},
    } = useForm<UserDataForm>({
        mode: 'onChange',
    })

    const resetErrorNotification = () => {
        setShowNotification(false)
        setNotificationText('')
    }

    const recoverCoupon = (userData: UserDataForm) => {
        setLoadingRecoverForm(true)

        axios
            .get(
                `${backendUrl}/${Endpoints.COUPONS}/${campaignCode}/${userData.plate}/${Endpoints.EMAIL}`
            )
            .then(() => {
                setLoadingRecoverForm(false)
                dispatch(showThanksRecoverPage(true))

                trackingEvent(
                    TrackingAction.VW_BASIC_FORM_SUBMISSION_SUCCESS_MESSAGE_LOAD,
                    {
                        configuration: {
                            CarlineName: defaultModel(),
                        },
                        eventInfo: {
                            ...eventInfoCommon(),
                            contentId: recoveryTitle,
                        },
                        form: {
                            FormName:
                                store.getState().configurationPromo
                                    .configuration.tracking_application_id +
                                ' - ' +
                                recoveryTitle,
                            FormStart: false,
                            FormType: trackingData.FORM_TYPE,
                            CycleID: cycleID,
                        },
                        process: {
                            StepName: trackingData.STEP_NAME,
                            StepNumber: trackingData.STEP_NUMBER,
                        },
                        user: {
                            ...getDealerUserInfo(),
                        },
                    }
                )
                resetErrorNotification()
                reset()
            })
            .catch((err) => {
                setLoadingRecoverForm(false)
                const statusCode = err?.response?.status

                if (statusCode === Global.ERROR_404) {
                    setError(Global.PLATE, {
                        type: 'manual',
                        message: t('layers.alertSystem.textFailedRecover'),
                    })

                    trackingError(Global.PLATE)
                } else if (!err?.response) {
                    openAlertSystemLayer(
                        t('layers.alertSystem.textErrorNetwork')
                    )
                } else {
                    openAlertSystemLayer(
                        t('layers.alertSystem.textFailedRecover')
                    )
                }
            })
    }

    const trackingBasicClick = () => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_CTA_CLICK, {
            configuration: {
                CarlineName: defaultModel(),
            },
            eventInfo: {
                ...eventInfoCommon(),
                contentId: recoveryTitle,
            },
            form: {
                FormName:
                    store.getState().configurationPromo.configuration
                        .tracking_application_id +
                    ' - ' +
                    recoveryTitle,
                FormType: trackingData.FORM_TYPE,
            },
            link: {
                name: recoveryCTA,
                url: window.location.href,
            },
        })
    }

    const trackingError = (errors: any) => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_ERROR_MESSAGE_LOAD, {
            form: {
                ErrorFormFields: [errors],
                CycleID: cycleID,
            },
        })
    }

    const onSubmit = (data: UserDataForm) => {
        resetErrorNotification()
        recoverCoupon(data)
        trackingBasicClick()
    }

    const onError = () => {
        trackingBasicClick()
        trackingError(Global.PLATE)
    }

    return (
        <>
            <LayerForm
                id={RECOVER_COUPON_FORM_ID}
                onSubmit={handleSubmit(onSubmit, onError)}
            >
                <ErrorSummary
                    errors={errors}
                    isSubmitted={formState.isSubmitted}
                />
                <FieldContainer>
                    <TextInput
                        {...register(Global.PLATE, {
                            required: {
                                value: true,
                                message: t('form.error.required'),
                            },
                        })}
                        appearance={
                            errors.plate ? InputStyle.Error : InputStyle.Default
                        }
                        message={
                            errors.plate ? errors.plate.message : undefined
                        }
                        label={t('recover.plate')}
                    />
                </FieldContainer>

                {showNotification ? (
                    <>
                        <Infobox
                            aria-live="assertive"
                            appearance={InfoboxStyle.Error}
                        >
                            <Text tag={TextTag.p}>{NotificationText}</Text>
                        </Infobox>
                    </>
                ) : undefined}
            </LayerForm>

            {loadingRecoverForm ? <Loading /> : undefined}
        </>
    )
}
